import { useCallback } from "react";
import { Search, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface SearchBoxProps {
  value: string;
  onChange: (value: string) => void;
  isSearching?: boolean;
  placeholder?: string;
  className?: string;
  onFocus?: () => void;
}

/**
 * Search input with icon and optional loading indicator.
 * Debouncing is handled by the parent hook (useSearch).
 */
export function SearchBox({
  value,
  onChange,
  isSearching = false,
  placeholder = "Search projects, workflows, and teams...",
  className,
  onFocus,
}: SearchBoxProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    onChange("");
  }, [onChange]);

  return (
    <div className={cn("relative", className)} role="search">
      {isSearching ? (
        <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
      ) : (
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      )}
      <Input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onFocus={onFocus}
        className={cn(
          "pl-9 w-[260px] h-9 transition-all focus:border-accent focus:ring-accent/30",
          value && "pr-9 border-accent ring-1 ring-accent/30",
        )}
        style={{
          backgroundColor: "var(--color-background)",
          borderRadius: "var(--radius-default)",
        }}
        aria-label="Search projects, workflows, and teams"
      />
      {value && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Clear search"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
